import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const exportToCSV = (data, filename, columns) => {
  if (!data.length) return;
  const headers = columns.map(c => c.label).join(',');
  const rows = data.map(row =>
    columns.map(c => {
      const val = c.key.split('.').reduce((o, k) => o?.[k], row) ?? '';
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(',')
  );
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const fallbackPrintPDF = (reportTitle, data, columns, summaryStats) => {
  const styles = `
    body { font-family: Arial, sans-serif; color: #1e293b; margin: 0; padding: 24px; }
    h1 { font-size: 22px; color: #1e40af; margin-bottom: 4px; }
    .meta { font-size: 12px; color: #64748b; margin-bottom: 20px; }
    .stats { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
    .stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; min-width: 120px; }
    .stat-val { font-size: 22px; font-weight: 700; color: #1e293b; }
    .stat-label { font-size: 11px; color: #64748b; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #1e40af; color: white; padding: 8px 10px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
    td { padding: 7px 10px; border-bottom: 1px solid #f1f5f9; }
    tr:nth-child(even) td { background: #f8fafc; }
    .footer { margin-top: 20px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 12px; }
  `;

  const statsHtml = summaryStats ? `
    <div class="stats">
      ${summaryStats.map(s => `<div class="stat"><div class="stat-val">${s.value}</div><div class="stat-label">${s.label}</div></div>`).join('')}
    </div>` : '';

  const theadHtml = `<tr>${columns.map(c => `<th>${c.label}</th>`).join('')}</tr>`;
  const tbodyHtml = data.map(row =>
    `<tr>${columns.map(c => {
      const val = c.key.split('.').reduce((o, k) => o?.[k], row) ?? '—';
      return `<td>${val}</td>`;
    }).join('')}</tr>`
  ).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${reportTitle}</title><style>${styles}</style></head>
    <body>
      <h1>${reportTitle}</h1>
      <div class="meta">Generated: ${new Date().toLocaleString()} · CyberGuard Security Awareness Platform</div>
      ${statsHtml}
      <table><thead>${theadHtml}</thead><tbody>${tbodyHtml}</tbody></table>
      <div class="footer">CyberGuard · Confidential · For internal use only</div>
    </body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.print(); }, 500);
};

export const exportToPDF = (reportTitle, data, columns, summaryStats) => {
  try {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175); // text-blue-800
    doc.text(reportTitle, 14, 22);
    
    // Meta Information
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139); // text-slate-500
    doc.text(`Generated: ${new Date().toLocaleString()} · CyberGuard Security Awareness Platform`, 14, 30);
    
    let startY = 40;
    
    // Add Summary Stats if available
    if (summaryStats && summaryStats.length > 0) {
      let xOffset = 14;
      summaryStats.forEach((stat) => {
        if (xOffset > 250) {
          xOffset = 14;
          startY += 15;
        }
        doc.setFontSize(12);
        doc.setTextColor(30, 41, 59); // text-slate-800
        doc.setFont(undefined, 'bold');
        doc.text(`${stat.value}`, xOffset, startY);
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 116, 139); // text-slate-500
        doc.text(`${stat.label}`, xOffset, startY + 5);
        
        xOffset += 45;
      });
      startY += 15;
    }
    
    // Prepare table data
    const tableColumns = columns.map(c => c.label);
    const tableRows = data.map(row => 
      columns.map(c => {
        const val = c.key.split('.').reduce((o, k) => o?.[k], row);
        return val !== null && val !== undefined ? String(val) : '—';
      })
    );
    
    // Add Table
    doc.autoTable({
      startY: startY,
      head: [tableColumns],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 8, textColor: 50 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { top: 14, right: 14, bottom: 20, left: 14 },
      didDrawPage: function (data) {
        // Footer
        const str = 'CyberGuard · Confidential · For internal use only';
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        doc.text(str, data.settings.margin.left, pageHeight - 10);
      }
    });
    
    // Save the PDF
    doc.save(`${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error("Error generating PDF, falling back to print format:", error);
    fallbackPrintPDF(reportTitle, data, columns, summaryStats);
  }
};